import { useQuery } from "@tanstack/react-query";
import { getApiInstanceForEntity } from "./config";
import { ApiFilters, Setting } from "../store/store.types";
import { useStore } from "../store/store";
import { useEffect } from "react";
import { FormatedFields } from "../types/fields.types";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import { OpenInNewTab } from "../components/datagrid/OpenInNewTab";
import { ContextType, useRecordsContext } from "../utils/useRecordsContext";
import { uniqBy } from "lodash";
import { mergeFieldsWithGridConfig } from "../components/datagrid/utils";
import { Filter } from "../components/filters/Filters";
import { isDynamicDateValue, transformRelativeDate } from "../components/filters/dynamic-fields/dynamicDateUtils";
import { DYNAMIC_USER_FIELDS, isDynamicUserFilter, transformRelativeUser } from "../components/filters/dynamic-fields/dynamicUserUtils";
import { useUser } from "./useAuth";
import { UserFromAPI } from "../types/auth.types";

export const useSettings = (entity: string, contextType: ContextType) => {
    const {
        recordsFilter,
        setRecordsFilter,
        modalFilter,
        setModalFilters,
        progressFilter,
        setProgressFilter,
        gridSettings,
        recordsSort,
        setRecordsSort,
    } = useStore();
    const { formatedFields: fields, dataGridOptions } = useRecordsContext(contextType);
    const {data: user} = useUser()

    const modalFilterFromSettings = gridSettings.selectedSettings?.config?.filters?.modal;
    const progressFilterFromSettings = gridSettings.selectedSettings?.config?.filters?.progress;
    const recordsFilterFromSettings = gridSettings.selectedSettings?.config?.filters?.records;
    const sortFromSettings = gridSettings.selectedSettings?.config?.sort;

    const { data, status } = useQuery({
        queryKey: [`settings-${entity}`],
        queryFn: () => getSettings(entity),
        staleTime: Infinity,
    });

    useEffect(() => {
        // Merge api fields with front end fields for columns
        const prepareMerge = [...fields] as GridColDef[];

        prepareMerge.unshift({
            headerName: "Nova tab",
            field: "new_tab",
            width: 75,
            renderCell: (params: GridCellParams) => (
                <OpenInNewTab params={params} entity={entity} customNavigation={dataGridOptions?.navigateToDetail} />
            ),
        });

        if (dataGridOptions && dataGridOptions.extraColumns) {
            if (dataGridOptions.extraColumns.beggining) prepareMerge.unshift(...dataGridOptions.extraColumns.beggining);
            if (dataGridOptions.extraColumns.end) prepareMerge.push(...dataGridOptions.extraColumns.end);
        }

        const uniqColumns = uniqBy(prepareMerge, "field") as FormatedFields;

        const apiSettings = data as Setting[];

        if (apiSettings) {
            let setting = apiSettings.find((item: Setting) => item.id === gridSettings.selectedSettings?.id) as Setting;

            if (!setting) {
                setting =
                    (apiSettings.find((item: Setting) => item.isActive) as Setting) ||
                    (apiSettings.find((item: Setting) => item.isSystem) as Setting);
            }

            const columnsConfig = mergeFieldsWithGridConfig(uniqColumns as FormatedFields, setting?.config?.columns);

            const shouldUpdateGridSettings = 
                gridSettings?.gridColumns?.length === 0 ||
                entity !== gridSettings.entity ||
                gridSettings?.apiSettings?.length !== apiSettings.length ||
                gridSettings?.activeOnGrid !== setting.id;

            if (shouldUpdateGridSettings && user) {
                gridSettings.setGridSettings({
                    gridColumns: columnsConfig,
                    selectedSettings: setting,
                    entity: entity,
                    apiSettings: apiSettings,
                    activeOnGrid: setting.id,
                });

                // Use filters from the NEW setting, not from the old gridSettings.selectedSettings
                const newModalFilterFromSettings = setting?.config?.filters?.modal;
                const newProgressFilterFromSettings = setting?.config?.filters?.progress;
                const newRecordsFilterFromSettings = setting?.config?.filters?.records;
                const newSortFromSettings = setting?.config?.sort;

                // Transform and set filters when view switches
                const { modalFilter, recordsFilter } = transformDynamicFilters(newModalFilterFromSettings as Array<Filter> | null | undefined, newRecordsFilterFromSettings, user);

                setModalFilters(modalFilter as ApiFilters | null);
                setProgressFilter(newProgressFilterFromSettings || null);
                setRecordsFilter(recordsFilter || null);
                setRecordsSort(newSortFromSettings || null);
            }
        }
    }, [
        data,
        dataGridOptions,
        entity,
        fields,
        gridSettings,
        modalFilterFromSettings,
        progressFilterFromSettings,
        recordsFilterFromSettings,
        sortFromSettings,
        setModalFilters,
        setProgressFilter,
        setRecordsFilter,
        setRecordsSort,
        recordsSort,
        status,
        user,
    ]);

    useEffect(() => {
        if (modalFilterFromSettings && !modalFilter) {
            setModalFilters(modalFilterFromSettings);
        }

        if (progressFilterFromSettings && !progressFilter) {
            setProgressFilter(progressFilterFromSettings);
        }

        if (!recordsFilter && recordsFilterFromSettings && user) {
            // Transform dynamic filters before setting (same as first useEffect)
            const { recordsFilter: transformedRecordsFilter } = transformDynamicFilters(
                modalFilterFromSettings as Array<Filter> | null | undefined,
                recordsFilterFromSettings,
                user
            );
            setRecordsFilter(transformedRecordsFilter || null);
        }

        if (!recordsSort && sortFromSettings) {
            setRecordsSort(sortFromSettings);
        }
    }, [
        entity,
        modalFilter,
        modalFilterFromSettings,
        progressFilter,
        progressFilterFromSettings,
        recordsFilter,
        recordsFilterFromSettings,
        recordsSort,
        sortFromSettings,
        setModalFilters,
        setProgressFilter,
        setRecordsFilter,
        setRecordsSort,
        user,
    ]); 
};

export const getSettings = async (entity: string) => {
    const apiInstance = getApiInstanceForEntity(entity);

    const filter = {
        _and: [
            {
                _or: [
                    {
                        user_created: {
                            id: {
                                _eq: "$CURRENT_USER",
                            },
                        },
                    },
                    {
                        isSystem: {
                            _eq: true,
                        },
                    },
                    {
                        shared_roles: {
                            directus_roles_id: {
                                users: {
                                    id: {
                                        _eq: "$CURRENT_USER",
                                    },
                                },
                            },
                        },
                    },
                ],
            },
            {
                collection: {
                    _eq: entity,
                },
            },
        ],
    };
    const fieldsQuery = "*.*,shared_roles.*,shared_roles.directus_roles_id.name,shared_roles.directus_roles_id.id,directus_roles_id.*";
    const response = await apiInstance({
        method: "GET",
        url: `/items/settings?filter=${JSON.stringify(filter)}&fields=${fieldsQuery}`,
    });
    const settings = response.data.data as Array<Setting>;
    settings.sort((a, b) => {
        const aIsSystem = a.isSystem ? 1 : 0;
        const bIsSystem = b.isSystem ? 1 : 0;
        if (aIsSystem !== bIsSystem) return bIsSystem - aIsSystem;
        if (a.isSystem && b.isSystem) {
            const aOrder = a.config?.orderOfSetting ?? Infinity;
            const bOrder = b.config?.orderOfSetting ?? Infinity;
            return aOrder - bOrder;
        }
        return 0;
    });
    return settings;
};

const transformDynamicFilters = (
	modalFilter: Array<Filter> | null | undefined,
	recordsFilter: ApiFilters | null | undefined,
	user: UserFromAPI
) => {
	if (!modalFilter || !recordsFilter) return { modalFilter, recordsFilter };

	const dynamicDateModalFilters = modalFilter.filter((mFilter) => {
		const isDynamic = isDynamicDateValue(mFilter.value);
		return isDynamic;
	});
	const dynamicUserModalFilters = modalFilter.filter((mFilter) => DYNAMIC_USER_FIELDS.includes(mFilter.field.field));

	if (dynamicDateModalFilters.length === 0 && dynamicUserModalFilters.length === 0)
		return { modalFilter, recordsFilter };

	const entity = Object.keys(recordsFilter)[0];
	const filtersByEntity = recordsFilter[entity];
	const operator = Object.keys(filtersByEntity)[0];
	const filtersFromRecords = filtersByEntity[operator];

	const transformedFilters = [];

	for (const recordFilter of filtersFromRecords) {
		if ("_or" in recordFilter) {
			const transformedOrFilters = (recordFilter._or as Array<Record<string, unknown>>).map((orFilter) =>
				transformSingleRecordFilter(orFilter, dynamicDateModalFilters, dynamicUserModalFilters, user)
			);
			transformedFilters.push({ _or: transformedOrFilters });
		} else {
			const transformedFilter = transformSingleRecordFilter(recordFilter, dynamicDateModalFilters, dynamicUserModalFilters, user);
			transformedFilters.push(transformedFilter);
		}
	}
	const finalFilter = { [entity]: { [operator]: transformedFilters } };
	return { modalFilter, recordsFilter: finalFilter };
};

const transformSingleRecordFilter = (
	recordFilter: Record<string, unknown>,
	dynamicDateModalFilters: Array<Filter>,
	dynamicUserModalFilters: Array<Filter>,
	user: UserFromAPI
) => {
	const field = getFilterField(recordFilter);

	const matchingModalFilterForDate = dynamicDateModalFilters.find((modalFilter) => modalFilter.field.field === field);
	const matchingModalFilterForUser = dynamicUserModalFilters.find(
		(modalFilter) => modalFilter.field.field === field && isDynamicUserFilter(modalFilter.value)
	);

	if (matchingModalFilterForDate) {
		return transformRelativeDate(matchingModalFilterForDate, recordFilter);
	} else if (matchingModalFilterForUser) {
		return transformRelativeUser(matchingModalFilterForUser, recordFilter, user);
	}
	return recordFilter;
};

const getFilterField = (filter: Record<string, unknown>): string => {
    const field = "_and" in filter ? Object.keys((filter._and as Array<Record<string, unknown>>)[0])[0] : Object.keys(filter)[0];
    return field;
}
